import { Formatters } from '../shared/lib/formatters';
import { LoadingSpinner } from '../shared/components';
import { emptyState } from '../shared/components/Narrative';

// SOP Library (PDF Notes(10) #9) — the company's single source of truth for "how
// we do things". Browse/search SOPs by area, read them rendered, edit in place,
// and create new ones with a one-click AI draft (POST /api/assetops/sops/draft).
// Backed by the `sops` app_settings blob via /api/assetops/sops. Jarvis (#10/#11)
// answers questions grounded in this same corpus.
const API = '/api/assetops';

const AREA_TONE: Record<string, string> = {
  Onboarding: '#1d4ed8', Housekeeping: '#0891b2', Maintenance: '#b45309', Inspections: '#7c3aed',
  'Guest Experience': '#be185d', Revenue: '#15803d', 'Owner Relations': '#0369a1', Finance: '#166534',
  Supplies: '#9a3412', General: '#475569',
};

export class SopsPage {
  private container: HTMLElement;
  private data: { areas: string[]; sops: any[] } | null = null;
  private guideShown: Map<string, boolean> | null = null;   // R88 #5: sop id → printed on the cleaning guidebook (global default)
  private guideBusy = '';
  private q = '';
  private areaFilter = 'all';
  private selectedId: string | null = null;
  private mode: 'view' | 'edit' | 'new' = 'view';
  private drafting = false;
  private saving = false;
  private note = '';
  // working copy fields for edit/new
  private fTitle = '';
  private fArea = 'General';
  private fContent = '';
  private fLinks = '';
  private fNotes = '';
  private fParentId: string | null = null; // set when creating a sub-page

  constructor(container: HTMLElement) { this.container = container; }

  private esc(s: any): string { return Formatters.escapeHtml(s == null ? '' : String(s)); }

  private inline(s: string): string {
    return Formatters.escapeHtml(s)
      .replace(/\*\*(.+?)\*\*/g, '<b>$1</b>')
      .replace(/`(.+?)`/g, '<code>$1</code>');
  }

  // Minimal Markdown → HTML for the SOP body (headings, bold, code, lists).
  private md(src: string): string {
    const lines = String(src || '').split('\n');
    let html = ''; let inList = false; let listType: 'ol' | 'ul' = 'ul';
    const closeList = () => { if (inList) { html += listType === 'ol' ? '</ol>' : '</ul>'; inList = false; } };
    for (const raw of lines) {
      const line = raw.replace(/\s+$/, '');
      let m: RegExpMatchArray | null;
      if ((m = line.match(/^#{2,3}\s+(.*)/))) { closeList(); html += `<div class="sop-h">${this.inline(m[1])}</div>`; continue; }
      if ((m = line.match(/^#\s+(.*)/))) { closeList(); html += `<div class="sop-h">${this.inline(m[1])}</div>`; continue; }
      if ((m = line.match(/^\s*\d+[.)]\s+(.*)/))) { if (!inList || listType !== 'ol') { closeList(); html += '<ol class="sop-ol">'; inList = true; listType = 'ol'; } html += `<li>${this.inline(m[1])}</li>`; continue; }
      if ((m = line.match(/^\s*[-*•]\s+(.*)/))) { if (!inList || listType !== 'ul') { closeList(); html += '<ul class="sop-ul">'; inList = true; listType = 'ul'; } html += `<li>${this.inline(m[1])}</li>`; continue; }
      if (!line.trim()) { closeList(); continue; }
      closeList(); html += `<p class="sop-p">${this.inline(line)}</p>`;
    }
    closeList();
    return html || '<p class="sop-p" style="color:var(--text-muted)">No content yet.</p>';
  }

  // R88 #5 (Jesse: "for ALL SOPs, a button that allows me to select if I want
  // it displayed in the cleaning guidebook") — global default; per-property
  // overrides stay on the property's Guide content panel.
  private async loadGuideShown(): Promise<void> {
    try {
      const j = await fetch('/api/ob/guide-sops').then(r => r.ok ? r.json() : null);
      if (j && Array.isArray(j.sops)) this.guideShown = new Map(j.sops.map((x: any) => [String(x.id), !!x.shown]));
      else this.guideShown = null;
    } catch { this.guideShown = null; }
  }

  async render(): Promise<void> {
    this.container.innerHTML = `<div class="page-content"><div id="sop-body"><div style="padding:40px; text-align:center;">${LoadingSpinner.render()}</div></div></div>`;
    await Promise.all([this.load(), this.loadGuideShown()]);
    this.draw();
  }

  private async load(): Promise<void> {
    this.data = await fetch(`${API}/sops`).then(r => r.ok ? r.json() : null).catch(() => null);
  }

  private filtered(): any[] {
    const all: any[] = this.data?.sops || [];
    const q = this.q.trim().toLowerCase();
    return all.filter(s =>
      (this.areaFilter === 'all' || s.area === this.areaFilter) &&
      (!q || String(s.title || '').toLowerCase().includes(q) || String(s.content || '').toLowerCase().includes(q)));
  }

  private selected(): any | null {
    return (this.data?.sops || []).find(s => s.id === this.selectedId) || null;
  }

  private openNew(parentId: string | null = null): void {
    this.mode = 'new'; this.selectedId = null; this.note = '';
    this.fParentId = parentId;
    const parent = parentId ? (this.data?.sops || []).find(s => s.id === parentId) : null;
    this.fTitle = ''; this.fArea = parent ? (parent.area || 'General') : (this.areaFilter !== 'all' ? this.areaFilter : 'General');
    this.fContent = ''; this.fLinks = ''; this.fNotes = '';
    this.draw();
  }

  private children(id: string): any[] {
    // server returns sub-pages already ordered (order, then title)
    return (this.data?.sops || []).filter(s => s.parent_id === id);
  }

  private openEdit(): void {
    const s = this.selected(); if (!s) return;
    this.mode = 'edit'; this.note = '';
    this.fParentId = s.parent_id || null;
    this.fTitle = s.title || ''; this.fArea = s.area || 'General';
    this.fContent = s.content || ''; this.fLinks = (s.links || []).join('\n');
    this.draw();
  }

  private syncFields(): void {
    const g = (id: string) => (this.container.querySelector<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>(id)?.value ?? '');
    this.fTitle = g('#sop-f-title'); this.fArea = g('#sop-f-area');
    this.fContent = g('#sop-f-content'); this.fLinks = g('#sop-f-links');
    const n = this.container.querySelector<HTMLTextAreaElement>('#sop-f-notes'); if (n) this.fNotes = n.value;
  }

  private async aiDraft(): Promise<void> {
    this.syncFields();
    if (!this.fTitle.trim()) { this.note = 'Add a title first so the draft has a topic.'; this.draw(); return; }
    this.drafting = true; this.note = ''; this.draw();
    try {
      const r = await fetch(`${API}/sops/draft`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ title: this.fTitle, area: this.fArea, notes: this.fNotes }),
      });
      const j = await r.json();
      if (!r.ok) throw new Error(j.error || 'draft failed');
      this.fContent = j.draft || '';
      this.note = 'AI draft inserted — review and edit, then Save.';
    } catch (e: any) {
      this.note = `Draft failed: ${e.message}`;
    } finally {
      this.drafting = false; this.draw();
    }
  }

  private async save(): Promise<void> {
    this.syncFields();
    if (!this.fTitle.trim()) { this.note = 'Title is required.'; this.draw(); return; }
    this.saving = true; this.note = ''; this.draw();
    try {
      const links = this.fLinks.split('\n').map(x => x.trim()).filter(Boolean);
      const payload: any = { title: this.fTitle, area: this.fArea, content: this.fContent, links };
      if (this.mode === 'edit' && this.selectedId) payload.id = this.selectedId;
      else if (this.fParentId) payload.parent_id = this.fParentId;
      const r = await fetch(`${API}/sops`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(payload),
      });
      const j = await r.json();
      if (!r.ok) throw new Error(j.error || 'save failed');
      await this.load();
      // the server echoes the saved record — select by its id (title matching
      // picks the wrong SOP when titles repeat across areas/books)
      if (j.sop && j.sop.id) this.selectedId = j.sop.id;
      this.mode = 'view'; this.note = 'Saved.';
    } catch (e: any) {
      this.note = `Save failed: ${e.message}`;
    } finally {
      this.saving = false; this.draw();
    }
  }

  private async remove(): Promise<void> {
    const s = this.selected(); if (!s) return;
    if (!confirm(`Delete the SOP "${s.title}"? This cannot be undone.`)) return;
    const parentId = s.parent_id || null;
    try {
      const r = await fetch(`${API}/sops/${encodeURIComponent(s.id)}/delete`, { method: 'POST' });
      const j = await r.json().catch(() => ({} as any));
      if (!r.ok) throw new Error(j.error || 'delete failed');
      // deleting a sub-page lands you back on its book, not the empty state
      this.selectedId = parentId; this.mode = 'view'; this.note = 'Deleted.';
      await this.load(); this.draw();
    } catch (e: any) { this.note = `Delete failed: ${e.message}`; this.draw(); }
  }

  private async move(id: string, dir: 'up' | 'down'): Promise<void> {
    await fetch(`${API}/sops/${encodeURIComponent(id)}/move`, {
      method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ dir }),
    }).catch(() => null);
    await this.load(); this.draw();
  }

  private areaPill(area: string): string {
    const c = AREA_TONE[area] || '#475569';
    return `<span class="sop-area" style="color:${c}; background:${c}1a; border-color:${c}40;">${this.esc(area || 'General')}</span>`;
  }

  private draw(): void {
    const body = document.getElementById('sop-body');
    if (!body) return;
    if (!this.data) { body.innerHTML = emptyState('SOP library unavailable', 'GET /api/assetops/sops failed — check the server log.', '⚠️'); return; }
    const sops: any[] = this.data.sops || [];
    const areas: string[] = this.data.areas || [];
    const rows = this.filtered();
    const areaCounts: Record<string, number> = {};
    sops.forEach(s => { areaCounts[s.area] = (areaCounts[s.area] || 0) + 1; });

    const areaChip = (val: string, label: string, n?: number) => {
      const on = this.areaFilter === val;
      return `<button class="sop-chip ${on ? 'on' : ''}" data-area="${this.esc(val)}">${this.esc(label)}${n != null ? ` <span class="sop-n">${n}</span>` : ''}</button>`;
    };

    // The list shows top-level SOPs ("books"); sub-pages live inside their
    // book's detail view. A search still surfaces matching sub-pages directly,
    // labeled with the book they belong to.
    const selBook = (() => { const s = this.selected(); return s ? (s.parent_id || s.id) : null; })();
    const tops = rows.filter(s => !s.parent_id);
    const subMatches = this.q.trim() ? rows.filter(s => s.parent_id) : [];
    const item = (s: any, inBook?: string) => {
      const on = this.mode === 'view' && (s.id === this.selectedId || (!inBook && s.id === selBook));
      const kids = this.children(s.id);
      const snippet = String(s.content || '').replace(/[#*`>-]/g, '').replace(/\s+/g, ' ').trim().slice(0, 90);
      return `<button class="sop-item ${on ? 'on' : ''}" data-sel="${this.esc(s.id)}">
        <div class="sop-item-top">${this.areaPill(s.area)}${kids.length ? `<span class="sop-link-n">📄 ${kids.length}</span>` : ''}${(s.links && s.links.length) ? `<span class="sop-link-n" ${kids.length ? 'style="margin-left:6px"' : ''}>🔗 ${s.links.length}</span>` : ''}</div>
        <div class="sop-item-title">${this.esc(s.title)}</div>
        ${inBook ? `<div class="sop-item-snip">in ${this.esc(inBook)}</div>` : (snippet ? `<div class="sop-item-snip">${this.esc(snippet)}…</div>` : '<div class="sop-item-snip" style="color:var(--text-faint)">empty</div>')}
      </button>`;
    };
    const listItems = tops.map(s => item(s)).join('')
      + subMatches.map(s => {
        const p = sops.find(x => x.id === s.parent_id);
        return item(s, p ? p.title : 'a book');
      }).join('');

    // RIGHT pane
    let detail = '';
    if (this.mode === 'edit' || this.mode === 'new') {
      const areaOpts = areas.map(a => `<option value="${this.esc(a)}" ${this.fArea === a ? 'selected' : ''}>${this.esc(a)}</option>`).join('');
      const parent = this.fParentId ? sops.find(x => x.id === this.fParentId) : null;
      detail = `
        <div class="sop-detail">
          ${parent ? `<div class="sop-crumb">📘 Sub-page of <b>${this.esc(parent.title)}</b></div>` : ''}
          <div class="sop-edit-head">
            <input id="sop-f-title" class="sop-in" placeholder="${parent ? 'Sub-page title (e.g. Step 3: Reconcile owner statements)' : 'SOP title (e.g. Standard Turnover Clean)'}" value="${this.esc(this.fTitle)}" />
            ${parent ? `<input type="hidden" id="sop-f-area" value="${this.esc(this.fArea)}">` : `<select id="sop-f-area" class="sop-sel">${areaOpts}</select>`}
          </div>
          <div class="sop-ai">
            <div class="sop-ai-row">
              <textarea id="sop-f-notes" class="sop-ta-sm" placeholder="Optional: notes / requirements for the AI to follow (e.g. 'we use Turno, 3 hr window, gate code in lockbox')">${this.esc(this.fNotes)}</textarea>
              <button class="sop-btn ai" id="sop-ai-btn" ${this.drafting ? 'disabled' : ''}>${this.drafting ? 'Drafting…' : '✦ Draft with AI'}</button>
            </div>
          </div>
          <label class="sop-lbl">Content (Markdown)</label>
          <textarea id="sop-f-content" class="sop-ta" placeholder="## Purpose&#10;…">${this.esc(this.fContent)}</textarea>
          <label class="sop-lbl">Reference links (one URL per line)</label>
          <textarea id="sop-f-links" class="sop-ta-sm" placeholder="https://…">${this.esc(this.fLinks)}</textarea>
          <div class="sop-actions">
            <button class="sop-btn primary" id="sop-save" ${this.saving ? 'disabled' : ''}>${this.saving ? 'Saving…' : 'Save SOP'}</button>
            <button class="sop-btn" id="sop-cancel">Cancel</button>
            ${this.mode === 'edit' ? `<button class="sop-btn danger" id="sop-del">Delete</button>` : ''}
            ${this.note ? `<span class="sop-note">${this.esc(this.note)}</span>` : ''}
          </div>
        </div>`;
    } else {
      const s = this.selected();
      if (!s) {
        detail = `<div class="sop-detail sop-empty">
          <div class="sop-empty-ic">📘</div>
          <div class="sop-empty-t">${sops.length ? 'Select an SOP to read it' : 'No SOPs yet'}</div>
          <div class="sop-empty-s">${sops.length ? 'Pick one from the list, or create a new one.' : 'Create your first SOP — write it yourself or let AI draft it.'}</div>
          <button class="sop-btn primary" id="sop-new-2">+ New SOP</button>
        </div>`;
      } else {
        const parent = s.parent_id ? sops.find(x => x.id === s.parent_id) : null;
        const kids = s.parent_id ? [] : this.children(s.id);
        const kidsHtml = s.parent_id ? '' : `
          <div class="sop-subs">
            <div class="sop-lbl" style="margin-top:0">Sub-pages${kids.length ? ` (${kids.length})` : ''}</div>
            ${kids.map((k: any, i: number) => `
              <div class="sop-sub-row">
                <button class="sop-sub-open" data-sel="${this.esc(k.id)}"><span class="sop-sub-n">${i + 1}.</span> ${this.esc(k.title)}</button>
                <button class="sop-mv" data-mv-up="${this.esc(k.id)}" title="Move up" ${i === 0 ? 'disabled' : ''}>▲</button>
                <button class="sop-mv" data-mv-dn="${this.esc(k.id)}" title="Move down" ${i === kids.length - 1 ? 'disabled' : ''}>▼</button>
              </div>`).join('')}
            <button class="sop-btn" id="sop-add-sub" style="margin-top:8px">+ Add sub-page</button>
          </div>`;
        detail = `
          <div class="sop-detail">
            ${parent ? `<button class="sop-crumb sop-crumb-link" data-sel="${this.esc(parent.id)}">‹ ${this.esc(parent.title)}</button>` : ''}
            <div class="sop-view-head">
              <div>
                <div class="sop-view-meta">${this.areaPill(s.area)}<span class="sop-upd">updated ${this.esc(this.fmtD(s.updated_at))}</span></div>
                <h2 class="sop-view-title">${this.esc(s.title)}</h2>
                ${(() => {
                  if (!String(s.content || '').trim()) return '';
                  if (!this.guideShown) return '';
                  const shown = this.guideShown.get(String(s.id)) ?? (s.area === 'Housekeeping');
                  const busy = this.guideBusy === String(s.id);
                  return `<div style="margin-top:6px; display:flex; align-items:center; gap:8px; font-size:12px;">
                    <span style="color:var(--text-muted);">📖 Cleaning guidebook:</span>
                    <button class="sop-btn" data-guide-toggle="${this.esc(s.id)}" ${busy ? 'disabled' : ''} style="padding:3px 12px; font-size:11.5px; ${shown ? 'background:var(--success-soft,#ecfdf5); border-color:var(--success,#16a34a); color:var(--success-text,#065f46);' : ''}" title="Whether this SOP prints on every property's cleaner guidebook (a property's Guide content panel can still override per home)">${busy ? 'saving…' : shown ? '✓ shown — click to hide' : 'hidden — click to show'}</button>
                  </div>`;
                })()}
              </div>
              <button class="sop-btn" id="sop-edit">Edit</button>
            </div>
            <div class="sop-content">${this.md(s.content)}</div>
            ${(s.links && s.links.length) ? `<div class="sop-links"><div class="sop-lbl">Reference links</div>${s.links.map((l: string) => `<a href="${this.esc(l)}" target="_blank" rel="noopener" class="sop-link">${this.esc(l)}</a>`).join('')}</div>` : ''}
            ${kidsHtml}
            ${this.note ? `<div class="sop-note" style="margin-top:10px">${this.esc(this.note)}</div>` : ''}
          </div>`;
      }
    }

    body.innerHTML = `
      <style>
        .sop-head { display:flex; align-items:baseline; gap:12px; margin-bottom:4px; }
        .sop-head h2 { margin:0; font-size:18px; font-weight:600; }
        .sop-head .sub { font-size:12px; color:var(--text-muted); }
        .sop-new { margin-left:auto; font-size:12px; font-weight:600; padding:7px 14px; border:none; border-radius:8px; background:var(--accent); color:#fff; cursor:pointer; }
        .sop-kpis { display:flex; gap:10px; margin:14px 0; flex-wrap:wrap; }
        .sop-kpi { min-width:100px; padding:10px 14px; border-radius:var(--radius); border:1px solid var(--border); background:#fff; }
        .sop-kpi-v { font-size:22px; font-weight:600; font-variant-numeric:tabular-nums; }
        .sop-kpi-l { font-size:10px; text-transform:uppercase; letter-spacing:.05em; color:var(--text-muted); }
        .sop-toolbar { display:flex; gap:8px; align-items:center; flex-wrap:wrap; margin-bottom:12px; }
        .sop-search { flex:1; min-width:200px; padding:8px 12px; border:1px solid var(--border); border-radius:8px; font-size:13px; }
        .sop-chips { display:flex; gap:6px; flex-wrap:wrap; margin-bottom:14px; }
        .sop-chip { font-size:11px; font-weight:600; padding:4px 11px; border:1px solid var(--border); border-radius:999px; background:#fff; color:var(--text-muted); cursor:pointer; }
        .sop-chip.on { background:var(--text); color:#fff; border-color:var(--text); }
        .sop-n { opacity:.55; font-weight:400; }
        .sop-grid { display:grid; grid-template-columns: 340px 1fr; gap:16px; align-items:start; }
        @media (max-width: 900px) { .sop-grid { grid-template-columns: 1fr; } }
        .sop-list { display:flex; flex-direction:column; gap:8px; max-height:70vh; overflow-y:auto; }
        .sop-item { text-align:left; background:#fff; border:1px solid var(--border); border-radius:10px; padding:11px 13px; cursor:pointer; transition:border-color .12s; }
        .sop-item:hover { border-color:#9ca3af; }
        .sop-item.on { border-color:var(--accent); box-shadow:0 0 0 1px var(--accent); }
        .sop-item-top { display:flex; align-items:center; gap:6px; margin-bottom:5px; }
        .sop-item-title { font-size:13px; font-weight:600; color:var(--text); }
        .sop-item-snip { font-size:11px; color:var(--text-muted); margin-top:2px; line-height:1.4; }
        .sop-area { display:inline-block; font-size:10px; font-weight:700; padding:2px 8px; border-radius:999px; border:1px solid; }
        .sop-link-n { font-size:10px; color:var(--text-faint); margin-left:auto; }
        .sop-detail { background:#fff; border:1px solid var(--border); border-radius:12px; padding:22px; min-height:300px; }
        .sop-empty { display:flex; flex-direction:column; align-items:center; justify-content:center; text-align:center; gap:8px; color:var(--text-muted); padding:60px 20px; }
        .sop-empty-ic { font-size:34px; } .sop-empty-t { font-size:15px; font-weight:600; color:var(--text); } .sop-empty-s { font-size:12px; max-width:320px; }
        .sop-view-head { display:flex; align-items:flex-start; justify-content:space-between; gap:12px; margin-bottom:14px; border-bottom:1px solid var(--border); padding-bottom:14px; }
        .sop-view-meta { display:flex; align-items:center; gap:8px; margin-bottom:6px; }
        .sop-upd { font-size:11px; color:var(--text-faint); }
        .sop-view-title { margin:0; font-size:20px; font-weight:700; }
        .sop-content { font-size:13.5px; line-height:1.65; color:var(--text); }
        .sop-content .sop-h { font-size:13px; font-weight:700; text-transform:uppercase; letter-spacing:.04em; color:var(--accent); margin:18px 0 6px; }
        .sop-content .sop-p { margin:0 0 9px; }
        .sop-content .sop-ol, .sop-content .sop-ul { margin:0 0 10px; padding-left:22px; }
        .sop-content li { margin:4px 0; }
        .sop-content code { background:var(--bg-subtle); padding:1px 5px; border-radius:4px; font-size:12px; }
        .sop-links { margin-top:16px; border-top:1px solid var(--border); padding-top:12px; }
        .sop-link { display:block; font-size:12px; color:var(--accent); text-decoration:none; margin:3px 0; word-break:break-all; }
        .sop-link:hover { text-decoration:underline; }
        .sop-lbl { display:block; font-size:11px; font-weight:600; text-transform:uppercase; letter-spacing:.04em; color:var(--text-muted); margin:12px 0 5px; }
        .sop-edit-head { display:flex; gap:8px; }
        .sop-in { flex:1; padding:9px 12px; border:1px solid var(--border); border-radius:8px; font-size:14px; font-weight:600; }
        .sop-sel { padding:9px 12px; border:1px solid var(--border); border-radius:8px; font-size:13px; background:#fff; }
        .sop-ai { margin-top:12px; background:var(--bg-subtle); border:1px solid var(--border); border-radius:10px; padding:10px; }
        .sop-ai-row { display:flex; gap:8px; align-items:stretch; }
        .sop-ta { width:100%; min-height:300px; padding:11px 13px; border:1px solid var(--border); border-radius:8px; font-size:13px; font-family:ui-monospace,SFMono-Regular,Menlo,monospace; line-height:1.5; resize:vertical; }
        .sop-ta-sm { width:100%; min-height:54px; padding:9px 12px; border:1px solid var(--border); border-radius:8px; font-size:12.5px; line-height:1.5; resize:vertical; }
        .sop-actions { display:flex; gap:8px; align-items:center; margin-top:14px; }
        .sop-btn { font-size:12px; font-weight:600; padding:8px 15px; border:1px solid var(--border); border-radius:8px; background:#fff; color:var(--text); cursor:pointer; }
        .sop-btn:hover { border-color:#9ca3af; }
        .sop-btn.primary { background:var(--accent); color:#fff; border-color:var(--accent); }
        .sop-btn.ai { background:#1e1b4b; color:#fff; border-color:#1e1b4b; white-space:nowrap; }
        .sop-btn.danger { color:var(--danger-text); border-color:var(--danger); margin-left:auto; }
        .sop-btn:disabled { opacity:.6; cursor:default; }
        .sop-note { font-size:11.5px; color:var(--text-muted); }
        .sop-crumb { font-size:12px; color:var(--text-muted); margin-bottom:10px; }
        .sop-crumb-link { display:inline-block; border:none; background:none; padding:0; cursor:pointer; color:var(--accent); font-weight:600; }
        .sop-crumb-link:hover { text-decoration:underline; }
        .sop-subs { margin-top:18px; border-top:1px solid var(--border); padding-top:14px; }
        .sop-sub-row { display:flex; align-items:center; gap:6px; margin:4px 0; }
        .sop-sub-open { flex:1; text-align:left; font-size:13px; font-weight:600; color:var(--text); background:var(--bg-subtle); border:1px solid var(--border); border-radius:8px; padding:9px 12px; cursor:pointer; }
        .sop-sub-open:hover { border-color:var(--accent); color:var(--accent); }
        .sop-sub-n { color:var(--text-faint); font-weight:400; margin-right:4px; }
        .sop-mv { font-size:10px; padding:6px 8px; border:1px solid var(--border); border-radius:6px; background:#fff; color:var(--text-muted); cursor:pointer; }
        .sop-mv:disabled { opacity:.35; cursor:default; }
      </style>
      <div class="sop-head">
        <h2>SOP Library</h2>
        <span class="sub">how we do things — one source of truth, searchable, AI-draftable</span>
        <button class="sop-new" id="sop-new">+ New SOP</button>
      </div>
      <div class="sop-kpis">
        <div class="sop-kpi"><div class="sop-kpi-v">${sops.length}</div><div class="sop-kpi-l">Total SOPs</div></div>
        <div class="sop-kpi"><div class="sop-kpi-v">${Object.keys(areaCounts).length}</div><div class="sop-kpi-l">Areas covered</div></div>
        <div class="sop-kpi"><div class="sop-kpi-v">${areas.length}</div><div class="sop-kpi-l">Areas defined</div></div>
      </div>
      <div class="sop-toolbar">
        <input class="sop-search" id="sop-search" placeholder="Search SOPs…" value="${this.esc(this.q)}" />
      </div>
      <div class="sop-chips">
        ${areaChip('all', 'All areas', sops.length)}
        ${areas.map(a => areaChip(a, a, areaCounts[a])).join('')}
      </div>
      <div class="sop-grid">
        <div class="sop-list">${listItems || `<div style="font-size:12px; color:var(--text-muted); padding:16px; text-align:center;">No SOPs match.</div>`}</div>
        ${detail}
      </div>`;
    this.bind();
  }

  private fmtD(s: string | null): string {
    if (!s) return '—';
    const d = new Date(s);
    return isNaN(d.getTime()) ? '—' : `${['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'][d.getUTCMonth()]} ${d.getUTCDate()}, ${d.getUTCFullYear()}`;
  }

  private bind(): void {
    const search = this.container.querySelector<HTMLInputElement>('#sop-search');
    search?.addEventListener('input', () => { this.q = search.value; this.draw(); search === document.activeElement; });
    // keep focus + caret after redraw on search
    if (search && this.q) { search.focus(); search.setSelectionRange(this.q.length, this.q.length); }

    this.container.querySelectorAll<HTMLElement>('[data-area]').forEach(el => el.addEventListener('click', () => {
      this.areaFilter = el.dataset.area!; this.draw();
    }));
    this.container.querySelectorAll<HTMLElement>('[data-guide-toggle]').forEach(el => el.addEventListener('click', async () => {
      const sid = el.dataset.guideToggle!;
      if (this.guideBusy || !this.guideShown) return;
      const cur = this.guideShown.get(sid) ?? ((this.selected()?.area) === 'Housekeeping');
      this.guideBusy = sid; this.draw();
      try {
        const r = await fetch('/api/ob/guide-sops', {
          method: 'POST', headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ sop_id: sid, scope: 'global', shown: !cur }),
        });
        const j = await r.json().catch(() => ({} as any));
        if (!r.ok) throw new Error(j.error || `HTTP ${r.status}`);
        this.guideShown.set(sid, !cur);
      } catch (e: any) { this.note = `Guidebook toggle failed: ${e.message}`; }
      this.guideBusy = ''; this.draw();
    }));
    this.container.querySelectorAll<HTMLElement>('[data-sel]').forEach(el => el.addEventListener('click', () => {
      this.selectedId = el.dataset.sel!; this.mode = 'view'; this.note = ''; this.draw();
    }));
    this.container.querySelector('#sop-new')?.addEventListener('click', () => this.openNew());
    this.container.querySelector('#sop-new-2')?.addEventListener('click', () => this.openNew());
    this.container.querySelector('#sop-add-sub')?.addEventListener('click', () => {
      const s = this.selected(); if (s && !s.parent_id) this.openNew(s.id);
    });
    this.container.querySelectorAll<HTMLButtonElement>('[data-mv-up]').forEach(el => el.addEventListener('click', () => void this.move(el.dataset.mvUp!, 'up')));
    this.container.querySelectorAll<HTMLButtonElement>('[data-mv-dn]').forEach(el => el.addEventListener('click', () => void this.move(el.dataset.mvDn!, 'down')));
    this.container.querySelector('#sop-edit')?.addEventListener('click', () => this.openEdit());
    this.container.querySelector('#sop-ai-btn')?.addEventListener('click', () => this.aiDraft());
    this.container.querySelector('#sop-save')?.addEventListener('click', () => this.save());
    this.container.querySelector('#sop-del')?.addEventListener('click', () => this.remove());
    this.container.querySelector('#sop-cancel')?.addEventListener('click', () => {
      this.syncFields();
      if (this.mode === 'new' && this.fParentId) this.selectedId = this.fParentId;
      this.mode = 'view'; this.note = ''; this.draw();
    });
  }
}
